import React, { useRef, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import '../../styles/Ppulbatu/2Photobooth.css';
import '../../styles/IceCream/IceCream.css';
import { getPhotoAreas } from './photoAreas';

const YT_VIDEO_ID = 'ihvuwqlGHXs';

function ResultScreenIceCream() {
  const location = useLocation();
  const { images = [], selectedFrame } = location.state || {};
  const canvasRef = useRef(null);
  const [canvasReady, setCanvasReady] = useState(false);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const imageData = canvas.toDataURL('image/png');
      if (imageData === 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==') return;
      const link = document.createElement('a');
      link.download = 'photostrip.png';
      link.href = imageData;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

    const calculateCropDimensions = (imgWidth, imgHeight, targetWidth, targetHeight) => {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    };

    function drawPlaceholder(area) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(area.x, area.y, area.width, area.height);
      ctx.fillStyle = '#999';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', area.x + area.width/2, area.y + area.height/2);
    }

    if (isMobile) {
      const frameImg = new window.Image();
      frameImg.src = selectedFrame?.frameImage;

      frameImg.onload = () => {
        const maxWidth = Math.min(600, window.innerWidth - 40);
        const scaleFactor = maxWidth / frameImg.naturalWidth;
        const mobileWidth = Math.round(frameImg.naturalWidth * scaleFactor);
        const mobileHeight = Math.round(frameImg.naturalHeight * scaleFactor);

        canvas.width = mobileWidth;
        canvas.height = mobileHeight;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const photoAreas = getPhotoAreas(mobileWidth, mobileHeight);

        let imagesLoaded = 0;
        const totalImages = images.length;

        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const area = photoAreas[i];

          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
                img.naturalWidth, img.naturalHeight, area.width, area.height
              );
              ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, area.x, area.y, area.width, area.height);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
                setCanvasReady(true);
              }
            };
            img.onerror = () => {
              drawPlaceholder(area);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
                setCanvasReady(true);
              }
            };
          } else {
            drawPlaceholder(area);
          }
        }

        if (totalImages === 0) {
          ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
          setCanvasReady(true);
        }
      };

      frameImg.onerror = () => {
        const mobileWidth = Math.min(400, window.innerWidth - 40);
        const mobileHeight = Math.round(mobileWidth * 4 / 3);
        canvas.width = mobileWidth;
        canvas.height = mobileHeight * 4;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const y = i * mobileHeight;
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => { ctx.drawImage(img, 0, y, mobileWidth, mobileHeight); };
          }
        }
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
        setCanvasReady(true);
      };

      return;
    }

    const frameImg = new window.Image();
    frameImg.src = selectedFrame?.frameImage;

    frameImg.onload = () => {
      canvas.width = frameImg.naturalWidth;
      canvas.height = frameImg.naturalHeight;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const frameWidth = frameImg.naturalWidth;
      const frameHeight = frameImg.naturalHeight;
      const photoAreas = getPhotoAreas(frameWidth, frameHeight);

      let imagesLoaded = 0;
      const totalImages = images.length;
      const drawFrameOnTop = () => {
        ctx.drawImage(frameImg, 0, 0);
        setCanvasReady(true);
      };

      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const area = photoAreas[i];

        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, img.naturalHeight, area.width, area.height
            );
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, area.x, area.y, area.width, area.height);
            imagesLoaded++;
            if (imagesLoaded === totalImages) { drawFrameOnTop(); }
          };
          img.onerror = () => {
            drawPlaceholder(area);
            imagesLoaded++;
            if (imagesLoaded === totalImages) { drawFrameOnTop(); }
          };
        } else {
          drawPlaceholder(area);
        }
      }

      if (totalImages === 0) { drawFrameOnTop(); }
    };

    frameImg.onerror = () => {
      const singlePhotoWidth = 900;
      const singlePhotoHeight = Math.round(singlePhotoWidth * 4 / 3);
      canvas.width = singlePhotoWidth;
      canvas.height = singlePhotoHeight * 4;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const y = i * singlePhotoHeight;
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, img.naturalHeight, singlePhotoWidth, singlePhotoHeight
            );
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, y, singlePhotoWidth, singlePhotoHeight);
          };
          img.onerror = () => { drawPlaceholder({ x: 0, y: y, width: singlePhotoWidth, height: singlePhotoHeight }); };
        } else {
          drawPlaceholder({ x: 0, y: y, width: singlePhotoWidth, height: singlePhotoHeight });
        }
      }
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
      setCanvasReady(true);
    };
  }, [images, selectedFrame]);

  return (
    <div className="ResultScreen IceCreamBg" style={{ backgroundImage: "url('/img/IceCream/Background.PNG')" }}>
      <div className="content-container">
        <div className="photo-frame-container">
          {images && images.length > 0 ? (
            <canvas
              ref={canvasRef}
              className="photo-strip"
              style={{
                maxHeight: '650px',
                width: 'auto',
                height: 'auto',
                border: 'none',
                boxShadow: 'none',
                background: 'transparent'
              }}
            />
          ) : (
            <p className="no-photos">No photos taken. Please try again.</p>
          )}
        </div>

        <div className="right-panel">
          <button
            onClick={handleDownload}
            className="download-button"
            disabled={!canvasReady}
            style={{
              opacity: canvasReady ? 1 : 0.5,
              cursor: canvasReady ? 'pointer' : 'not-allowed'
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="20"
              viewBox="0 0 24 24"
              width="20"
              fill="white"
              style={{ marginRight: '8px' }}
            >
              <path d="M5 20h14v-2H5v2zM12 2v12l4-4h-3V2h-2v8H8l4 4z" />
            </svg>
            Download Photostrip
          </button>

          <div className="hashtag-container">
            <p>Thanks for trying out the IceCream photobooth!</p>
            <p>Don't forget to share using <strong>#moa4cuts</strong> 🩵</p>
          </div>

          <div style={{ width: '80%', aspectRatio: '16 / 9', margin: '0 auto' }}>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube-nocookie.com/embed/${YT_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YT_VIDEO_ID}&playsinline=1&si=acLRkiwNjIEv4iKP`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>


          <Link to="/icecream" style={{ color: 'black', padding: '50px 12px' }}>
            🏠 Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResultScreenIceCream;
